import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllOrders, updateOrderDeliveryStatus } from 'store/features/orders/orderAction';
import {
  selectAllOrders,
  selectVendorLoading,
  selectVendorError,
  selectUpdating,
} from 'store/features/orders/orderSelectors';
import { FiPackage, FiCalendar, FiTruck, FiCheckCircle, FiClock, FiEdit } from 'react-icons/fi';
import { format } from 'date-fns';

const AllOrders = () => {
  const dispatch = useDispatch();
  const allOrders = useSelector(selectAllOrders);
  const loading = useSelector(selectVendorLoading);
  const error = useSelector(selectVendorError);
  const updating = useSelector(selectUpdating);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({});

  useEffect(() => {
    dispatch(fetchAllOrders());
  }, [dispatch]);

  const handleStatusChange = (orderId, currentStatus) => {
    setSelectedStatus(prev => ({ ...prev, [orderId]: currentStatus }));
    setEditingOrderId(orderId);
  };

  const handleStatusUpdate = async (orderId) => {
    try {
      await dispatch(updateOrderDeliveryStatus(orderId, selectedStatus[orderId]));
      setEditingOrderId(null);
      // Refresh orders to get the latest data
      dispatch(fetchAllOrders());
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const cancelEdit = (orderId) => {
    setEditingOrderId(null);
    setSelectedStatus(prev => ({ ...prev, [orderId]: allOrders.find(order => order.OrderId === orderId)?.deliveryStatus }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" /> Paid
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" /> Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  const getDeliveryStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'Ordered':
        return (
          <span className={`${baseClasses} bg-blue-100 text-blue-800`}>
            <FiClock className="mr-1" /> Ordered
          </span>
        );
      case 'Shipped':
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <FiTruck className="mr-1" /> Shipped
          </span>
        );
      case 'Delivered':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <FiCheckCircle className="mr-1" /> Delivered
          </span>
        );
      case 'Cancellation':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <FiClock className="mr-1" /> Cancelled
          </span>
        );
      default:
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            {status}
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded-lg">
        Error loading orders: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8  h-screen">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-800">All Orders (Admin)</h1>
        <div className="flex items-center space-x-4">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm">
            <span className="text-gray-700">Total Orders:</span>
            <span className="ml-2 font-semibold">{allOrders.length}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-indigo-600 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Customer Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Delivery Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allOrders.map((order) => (
                <tr key={order.OrderId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{order.OrderId?.slice(-6).toUpperCase()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {order.CustomerEmail}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <FiPackage className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {order.ProductsName?.length || 0} item{(order.ProductsName?.length || 0) !== 1 ? 's' : ''}
                        </div>
                        <div className="text-sm text-gray-500">
                          {order.ProductsName?.[0] || 'N/A'}
                          {(order.ProductsName?.length || 0) > 1 && ` +${(order.ProductsName?.length || 0) - 1} more`}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FiCalendar className="mr-2 text-gray-400" />
                      {order.Date ? format(new Date(order.Date), 'MMM dd, yyyy HH:mm') : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      ₹{order.Amount?.toFixed(2) || '0.00'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(order.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingOrderId === order.OrderId ? (
                      <select
                        value={selectedStatus[order.OrderId] || order.deliveryStatus}
                        onChange={(e) => setSelectedStatus(prev => ({ 
                          ...prev, 
                          [order.OrderId]: e.target.value 
                        }))}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      >
                        <option value="Ordered">Ordered</option>
                        <option value="Shipped">Shipped</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Cancellation">Cancellation</option>
                      </select>
                    ) : (
                      getDeliveryStatusBadge(order.deliveryStatus)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingOrderId === order.OrderId ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleStatusUpdate(order.OrderId)}
                          disabled={updating}
                          className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                        >
                          {updating ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => cancelEdit(order.OrderId)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(order.OrderId, order.deliveryStatus)}
                        className="text-indigo-600 hover:text-indigo-900 flex items-center"
                      >
                        <FiEdit className="mr-1" /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {allOrders.length === 0 && !loading && (
        <div className="text-center py-12">
          <FiPackage className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
          <p className="mt-1 text-sm text-gray-500">There are no orders in the system yet.</p>
        </div>
      )}
    </div>
  );
};

export default AllOrders;